import React, { createContext, useContext, useState, useEffect } from "react";
import { auth } from "../../services/firebase";
import { onAuthStateChanged, User } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from "../Toaster/Toaster";
import { userSignOut } from "../../services/auth.service";
import { getCookie } from "../../utils/cookies";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const userTokenExists = () => {
    const token = getCookie("ch_token");

    return token ? true : false;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);

      if (!user && location.pathname !== "/login" && location.pathname !== "/forgot-password" && location.pathname !== "/reset-password" && location.pathname !== "/sso" && location.pathname !== "/security/review" && location.pathname !== "/set-password") {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate, location.pathname]);

  useEffect(() => {
    const tokenCheckInterval = setInterval(() => {
      if (!userTokenExists() && location.pathname !== "/login" && location.pathname !== "/forgot-password" && location.pathname !== "/reset-password" && location.pathname !== "/sso" && location.pathname !== "/security/review" && location.pathname !== "/set-password") {
        showToast("Session has expired, please login again", { type: "error" });
        navigate("/login");
      }
    }, 1000); // Check every second

    return () => clearInterval(tokenCheckInterval);
  }, [navigate, location.pathname]);

  const logout = () => {
    userSignOut();
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
